import React, { FC } from 'react';
import G2Api from 'api/g2';
import { ApiClientError } from 'api/client';
import { useG2ReviewContext } from 'app/context/G2ReviewContext';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';
import IntercomUtils from 'app/utils/intercom';
import Logger from 'utils/logger';
import { DropdownMenu, Icon } from '@kontentino/ui';
import { faBook } from '@fortawesome/pro-regular-svg-icons/faBook';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import { faCompass } from '@fortawesome/pro-regular-svg-icons/faCompass';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { Link } from 'react-router-dom';
import VerticalNavigationDropdownOption from './UserActionDropdownOption';
import VerticalNavigationMenuOption from '../VerticalNavigationMenuOption';
import { VerticalMenuItemWrapper } from '../VerticalMenuItemWrapper';
import { Module } from 'config';
import { useTranslation } from 'react-i18next';
import { useToast } from 'app/hooks/useToast';

type Props = {};

const HelpCenter: FC<Props> = () => {
  const { t } = useTranslation();
  const { isEligible: showG2Review } = useG2ReviewTrigger();
  const { openG2ReviewModal } = useG2ReviewContext();
  const toast = useToast();

  const handleG2ReviewClick = () => {
    G2Api.getReviewFormUrl()
      .then(({ url }) => {
        openG2ReviewModal(url);
        G2Api.formDisplayed().catch((e) => Logger.error(e));
      })
      .catch((e: ApiClientError) => {
        Logger.error(e);
        toast(e?.userMessage || t('g2Review.loadReviewFormError'), 'error');
      });
  };

  return (
    <VerticalMenuItemWrapper>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger data-name="navbar_help-center">
          <VerticalNavigationMenuOption
            icon={<Icon size={18} icon={faQuestionCircle} />}
            label={t('navigation.userSubActions.helpCenter')}
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="right"
            sideOffset={-10}
            className="tw-z-[99999] tw-min-w-[244px]"
          >
            {showG2Review && (
              <DropdownMenu.Item
                data-name="navbar_help-center_g2-review"
                data-cy="navbar_help-center_g2-review"
                onSelect={handleG2ReviewClick}
              >
                <VerticalNavigationDropdownOption
                  icon="🔥"
                  label={t('g2Review.addReview')}
                />
              </DropdownMenu.Item>
            )}
            <Link to={Module.OldApp.URL.HELP_CENTER.TUTORIALS} target="_blank">
              <DropdownMenu.Item data-name="navbar_help-center_tutorials">
                <VerticalNavigationDropdownOption
                  icon={<Icon icon={faInfoCircle} />}
                  label={t('navigation.userSubActions.tutorials')}
                />
              </DropdownMenu.Item>
            </Link>
            <Link
              to={Module.OldApp.URL.HELP_CENTER.RESOURCE_CENTER}
              target="_blank"
            >
              <DropdownMenu.Item data-name="navbar_help-center_resource">
                <VerticalNavigationDropdownOption
                  icon={<Icon icon={faBook} />}
                  label={t('navigation.userSubActions.resourceCenter')}
                />
              </DropdownMenu.Item>
            </Link>

            <Link to={Module.OldApp.URL.HELP_CENTER.BOOK_DEMO} target="_blank">
              <DropdownMenu.Item data-name="navbar_help-center_demo">
                <VerticalNavigationDropdownOption
                  icon={<Icon icon={faCalendar} />}
                  label={t('navigation.userSubActions.bookDemo')}
                />
              </DropdownMenu.Item>
            </Link>
            <Link
              to={Module.OldApp.URL.HELP_CENTER.PRODUCT_ROADMAP}
              target="_blank"
            >
              <DropdownMenu.Item data-name="navbar_help-center_roadmap">
                <VerticalNavigationDropdownOption
                  icon={<Icon icon={faCompass} />}
                  label={t('navigation.userSubActions.productRoadmap')}
                />
              </DropdownMenu.Item>
            </Link>

            <DropdownMenu.Item
              data-name="navbar_help-center_help"
              onSelect={() =>
                IntercomUtils.newConversation('Hello! Just having a question')
              }
            >
              <VerticalNavigationDropdownOption
                icon={<Icon icon={faComment} />}
                label={t('navigation.userSubActions.getHelp')}
              />
            </DropdownMenu.Item>

            <DropdownMenu.Item
              data-name="navbar_help-center_report"
              onSelect={() =>
                IntercomUtils.newConversation(
                  'Hi! Having an issue with Kontentino here',
                )
              }
            >
              <VerticalNavigationDropdownOption
                icon={<Icon icon={faTriangleExclamation} />}
                label={t('navigation.userSubActions.reportIssue')}
              />
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </VerticalMenuItemWrapper>
  );
};

export default React.memo(HelpCenter);
